// trace-viewer/src/behaviors/rotatable.ts
// Direct imports
import { TraceBehavior } from "./tracebehavior";
import { RotatableData } from "../scenedata/behaviors/rotatabledata";
import { DragToRotate } from "../managers/DragToRotate";

/**
 * Rotatable behavior — enables drag-to-rotate interaction at runtime.
 *
 * When present on a SceneObject, the object can be dragged to rotate it.
 * Horizontal mouse movement rotates around Y, vertical movement rotates
 * around the camera-right axis. Optionally constrained to specific axes.
 *
 * Drag is only active during play mode. Grabbing any rotatable object
 * selects it via RuntimeSelectionManager.
 */
export class Rotatable extends TraceBehavior<RotatableData> {
    static readonly behaviorType = "Rotatable" as const;

    private _dragToRotate: DragToRotate | null = null;
    private _registeredForSelection = false;
    private _removeSelectionRegistration: (() => void) | null = null;

    protected override onAttach(): void {
        const scene = this.sceneObject.transform.getScene();
        this._dragToRotate = new DragToRotate(this.sceneObject.transform, scene, {
            axes: this._resolved.axes,
            momentumDamping: this._resolved.momentumDamping,
            onGrab: () => {
                this.sceneObject.traceScene.getRuntimeSelectionManager()
                    .select(this.sceneObject);
            },
        });
    }

    protected override onPlayModeChanged(playing: boolean): void {
        if (this._dragToRotate) {
            this._dragToRotate.enabled = playing;
        }

        if (playing) {
            this._registerForSelection();
        } else {
            this._unregisterForSelection();
        }
    }

    protected override onDetach(): void {
        this._unregisterForSelection();
        if (this._dragToRotate) {
            this._dragToRotate.dispose();
            this._dragToRotate = null;
        }
    }

    protected override async applyCommit(): Promise<void> {
        if (this._dragToRotate) {
            this._dragToRotate.setOptions({
                axes: this._resolved.axes,
                momentumDamping: this._resolved.momentumDamping,
            });
        }
    }

    private _registerForSelection(): void {
        if (this._registeredForSelection) return;
        this._registeredForSelection = true;

        this._removeSelectionRegistration = this.sceneObject.traceScene.getRuntimeSelectionManager().register(
            this.sceneObject,
            {
                onSelected: () => { /* no visual affordance yet */ },
                onDeselected: () => { /* no visual affordance yet */ },
            },
        );
    }

    private _unregisterForSelection(): void {
        if (!this._registeredForSelection) return;
        this._registeredForSelection = false;

        this._removeSelectionRegistration?.();
        this._removeSelectionRegistration = null;
    }
}
